<template>
  <div
    v-if="isHamburgerVisible"
    :key="collectionOrId"
    :class="{ 'dropdown-hamburger mb-2 me-2 col-xs-3 col-sm-6 col-md-auto flex-grow-1': isSmallScreen }"
  >
    <component :is="containerElement.component" v-bind="containerElement.binds" v-on="containerElement.events">
      <!-- Label for dropdown -->
      <template v-if="isSmallScreen">
        {{ $i18n.t('list_functions') }}
      </template>

      <component
        :is="isSmallScreen ? 'el-dropdown-menu' : 'div'"
        :slot="isSmallScreen ? 'dropdown' : 'default'"
        :class="{ 'grouped-hamburger flex-grow-1 mb-2 me-2 el-button-group': !isSmallScreen }"
      >
        <!--RowSelection Filter-->
        <template v-if="showRowSelectionOption">
          <template v-if="isSmallScreen">
            <el-divider content-position="left" class="black">{{ $i18n.t('list_row_selection_filter') }}</el-divider>
            <el-dropdown-item
              v-for="filter in SELECTED_ROW_OPTIONS"
              :key="filter.key"
              :command="{ payload: filter.key, type: 'rowFilter' }"
            >
              <span
                v-for="icon of filter.icon"
                class="material-icons md-12"
                :style="`width: ${filter.icon.length > 1 ? 12 : 24}px;`"
                :key="`option-${icon}`"
              >{{ icon }}</span
              >
              <span
                :class="{
                  'active-row-filter': selectedRowsFilter === filter.key,
                }"
              >{{ $i18n.t(filter.label) }}</span
              >
            </el-dropdown-item>
          </template>
          <el-dropdown
            v-else
            data-id="grouped_hamburger_filter"
            class="el-button el-button--default el-button--small is-plain p-0"
            :class="{ 'is-active-button': selectedRowsFilter !== 'normal' }"
            placement="bottom-start"
            @command="setSelectedRowsFilter"
            trigger="click"
            :title="$i18n.t('change_list_view_mode')"
          >
            <span class="el-dropdown-link">
              <span
                v-for="icon in selectedRowsFilterIcon"
                :key="icon"
                :style="`width: ${selectedRowsFilterIcon.length > 1 ? 16 : 32}px;`"
                class="material-icons md-18"
              >{{ icon }}</span
              >
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item
                v-for="el in SELECTED_ROW_OPTIONS"
                :key="el.label"
                class="d-flex align-items-center"
                :command="el.key"
              >
                <span
                  v-for="icon in el.icon"
                  :key="icon"
                  :style="`width: ${el.icon.length > 1 ? 16 : 32}px;`"
                  class="material-icons md-18"
                >{{ icon }}</span
                >
                <span class="ps-2 ms-auto" :class="{ 'active-selection': el.key === selectedRowsFilter }">{{
                    $i18n.t(el.label)
                  }}</span>
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </template>
        <!--Group Actions-->
        <template v-if="areGroupActionsAvailable">
          <el-divider v-if="isSmallScreen" content-position="left" class="black">{{
              $i18n.t('group_actions')
            }}</el-divider>
          <group-action
            :is-hamburger-dropdown="isSmallScreen"
            :is-modal-open="isGroupActionsOpen"
            :class="{ 'el-button el-button--small is-plain p-0': !isSmallScreen }"
            @toggle-modal-open="toggleGroupActionModal"
          />
        </template>
        <!--Export -->
        <component
          v-if="isExportAvailable"
          :is="childElement"
          :class="{ 'el-button clear-el-button clear-child-el-button': !isSmallScreen }"
        >
          <export-modal />
        </component>
        <!--Show In List -->
        <component
          v-if="showColumnSelector"
          :is="childElement"
          :class="{ 'el-button clear-el-button': !isSmallScreen }"
        >
          <modal-opener
            :id="modalIdShowColumns"
            data-id="grouped_hamburger_list"
            :title="$i18n.t('list_visible_columns')"
            :text="isSmallScreen"
            :class="{ 'el-button el-button--default el-button--small is-plain': !isSmallScreen }"
          >
            <span :class="{ 'material-icons md-18': !isSmallScreen }">
              <i v-if="isSmallScreen" class="material-icons md-18 icon-align">list</i>
              {{ isSmallScreen ? $i18n.t('list_visible_columns') : 'list' }}
            </span>
          </modal-opener>
        </component>
        <!--Change View Options -->
        <template v-if="Object.keys(views).length > 1">
          <template v-if="isSmallScreen">
            <el-divider content-position="left" class="black">{{ $i18n.t('list_views') }}</el-divider>
            <el-dropdown-item v-for="view in views" :key="view.name" :command="{ payload: view.name, type: 'view' }">
              <span :class="{ 'active-view': currentView === view }">
                <i class="material-icons md-18 icon-align">{{ view.icon }}</i
                >{{ $i18n.t(view.label) }}
              </span>
            </el-dropdown-item>
          </template>
          <el-dropdown
            v-else
            data-id="grouped_hamburger_change_view"
            class="el-button el-button--default el-button--small is-plain p-0"
            placement="bottom-start"
            @command="changeView"
            trigger="click"
            :title="$i18n.t('change_list_view_mode')"
          >
            <span class="el-dropdown-link">
              <span class="material-icons md-18">{{ currentView.icon }}</span>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item
                v-for="view in views"
                :key="view.name"
                class="d-flex align-items-center"
                :command="view.name"
              >
                <span class="material-icons md-18">{{ view.icon }}</span>
                <span class="ps-2" :class="{ 'active-selection': view.name === viewMode }">{{
                    $i18n.t(view.label)
                  }}</span>
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </template>
        <!--Saved Filter-->
        <component v-if="showSavedFilter" :is="childElement" :class="{ 'el-button clear-el-button': !isSmallScreen }">
          <saved-filter-opener :bare-link="isSmallScreen" />
        </component>
        <!--Reset Searchparams-->
        <el-dropdown-item
          v-if="showResetButton && isSmallScreen"
          :title="$i18n.t('list_filters_reset')"
          class="clearfix"
        >
          <span @click="resetParams">
            <i class="material-icons md-18 icon-align">close</i>{{ $i18n.t('list_buttons_reset') }}
          </span>
        </el-dropdown-item>
        <el-button
          v-else-if="showResetButton && !isSmallScreen"
          type="default"
          class="is-plain"
          size="small"
          :title="$i18n.t('list_filters_reset')"
          @click="resetParams"
        >
          <i class="material-icons md-18">filter_alt_off</i>
        </el-button>
        <!--Pagination Options-->
        <template v-if="showPaginationGroup">
          <el-divider v-if="isSmallScreen" content-position="left" class="black">{{
            $i18n.t('pagination_options')
          }}</el-divider>
          <component
            v-if="showListOrder"
            :is="childElement"
            :title="$i18n.t('list_order')"
            :class="{ clearfix: isSmallScreen, 'el-button p-0 border-0': !isSmallScreen }"
          >
            <list-order :in-dropdown="isSmallScreen" />
          </component>
          <component
            v-if="showPageLimiter"
            :is="childElement"
            :title="$i18n.t('list_page_limiter')"
            :class="{ clearfix: isSmallScreen, 'el-button p-0 border-0': !isSmallScreen }"
          >
            <limitation />
          </component>
        </template>
      </component>
    </component>
  </div>
</template>

<script>
  import mixinHamburger from './mixin-hamburger'
  import { LIST_HEADER_SMALL_SCREEN } from '../lib/list-constants'

  export default {
    name: 'ListHeaderMenu',
    mixins: [mixinHamburger],
    data() {
      return {
        screenWidth: window.innerWidth,
      }
    },
    computed: {
      isSmallScreen() {
        return this.screenWidth < LIST_HEADER_SMALL_SCREEN
      },
      containerElement() {
        return this.isSmallScreen
          ? {
              component: 'el-dropdown',
              binds: {
                class: 'hamburger-dropdown-menu',
                'split-button': true,
                type: 'primary',
                trigger: 'click',
                placement: 'bottom-start',
              },
              events: {
                command: this.handleCommand,
              },
            }
          : {
              component: 'el-button-group',
              binds: {},
              events: {},
            }
      },
      childElement() {
        return this.isSmallScreen ? 'el-dropdown-item' : 'div'
      },
      searchparamElement() {
        return this.isSmallScreen ? 'el-dropdown-item' : 'el-button'
      },
      showPaginationGroup() {
        return this.isSmallScreen ? this.showListOrder || this.showPageLimiter : this.isTemplatedListControl
      },
    },
    methods: {
      handleResize() {
        this.screenWidth = window.innerWidth
      },
    },
    mounted() {
      window.addEventListener('resize', this.handleResize)
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.handleResize)
    },
  }
</script>

<style lang="scss">
  .is-active-button {
    box-shadow: inset 0 0 4px $color-not-set;
  }
  .active-selection {
    color: $color-primary;
    font-weight: 600;
  }
  .active-view,
  .active-row-filter {
    color: $color-primary;
    font-weight: 600;
  }
  .hamburger-dropdown-menu {
    min-width: fit-content;
  }
  .el-divider__text {
    color: $color-text-regular;
  }
  .icon-align {
    vertical-align: sub;
  }

  div.grouped-hamburger {
    display: block;
    .el-dropdown-link {
      display: block;
      padding: 9px 15px;
    }
  }
  div.dropdown-hamburger {
    display: none;
  }

  @media all and (max-width: 994px) {
    div.grouped-hamburger {
      display: none;
    }

    div.dropdown-hamburger {
      display: flex;
    }
  }

  .el-button.clear-el-button {
    border: 0;
    padding: 0;
  }

  .clear-child-el-button .el-button {
    border-radius: 0;
  }
</style>
